import SiteDesignStore from "../stores/SiteDesignStore";
import { commandMap } from "./commandMap";
import { isDispatchMonitoringEnabled, monitorDispatch } from "./dispatchMonitor";
import { getDocumentCoreMode } from "./flag";
import {
  createInitialState,
  getCurrentStatePlain as computeCurrentStatePlain,
  setStatePlain as computeSetStatePlain,
} from "./documentState";

/**
 * @typedef {{ type: string, payload?: any }} DesignCommand
 */

/**
 * @typedef {import("./sideEffects").SIDE_EFFECTS} SideEffectTypeMap
 * @typedef {{ type: string, [key: string]: any }} SideEffect - a plain
 *  descriptor object; `type` is one of `SIDE_EFFECTS`'s values (see
 *  `sideEffects.js`). The order of a handler's returned array IS
 *  significant -- the Task 10 shim drains it in order.
 */

/**
 * Registry of owner-mode (`feature.documentCore` on) command handlers,
 * populated by domain handler modules (Tasks 3-9 of the Phase 5c Stage 1
 * plan). Each handler is a pure function `(state, payload) => result`,
 * where `state` is the DocumentStore-owned plain-JS shape
 * (`{design, page, selected, blockContainerType}`, see `documentState.js`)
 * and `result` is EITHER of two shapes (both normalized by
 * `normalizeHandlerResult`, below, before `applyCommand` uses them):
 * - bare next-state (Task 3's Class-A contract -- `{design, page, selected,
 *   blockContainerType}` directly, no side effects). All 16 already-landed
 *   Class-A handlers use this shape UNCHANGED.
 * - `{ nextState, sideEffects }` (Task 4a+ contract, for handlers whose
 *   legacy counterpart re-entrantly drove another store/action -- see
 *   `sideEffects.js`). `sideEffects` is an ordered array of `SideEffect`
 *   descriptors; empty/omitted means none.
 *
 * A handler MUST stay pure regardless of shape: no Reflux dispatches, no
 * reads of other stores -- data a legacy handler read from e.g.
 * `SelectionStore`/`GroupStore` must arrive via the command payload instead.
 *
 * Empty until later tasks register into it; `applyCommand` throws on an
 * unregistered type. Exported so handler modules can register, and so the
 * parity harness (`parityHarness.js`) can look up a handler by command type.
 * @type {Record<string, (state: object, payload: any) => (object | { nextState: object, sideEffects?: SideEffect[] })>}
 */
export const handlerRegistry = {};

/**
 * Owner mode needs its handlers registered, its state seeded and its side
 * effects drained. Whatever wires those up marks it ready; until then the
 * flag is ignored, since routing to a half-wired owner would throw or drift.
 */
let ownerModeReady = false;

/** @param {boolean} [ready] */
export const markOwnerModeReady = (ready = true) => {
  ownerModeReady = ready;
};

/**
 * Flag OFF (or owner mode not ready): the mapped `SiteActions` call
 * (pass-through). Flag ON: the command applied to the owned state via `applyCommand`.
 * @param {DesignCommand} command
 * @returns {*} whatever the mapped action returns (flag off)
 */
const route = (command) => {
  if (ownerModeReady && getDocumentCoreMode()) {
    return DocumentStore.applyCommand(command?.type, command?.payload);
  }
  const fn = commandMap[command?.type];
  if (!fn) {
    throw new Error(`DocumentStore: unknown command type "${command?.type}"`);
  }
  return fn(command.payload);
};

/**
 * Normalizes a handler's raw return value to `{ nextState, sideEffects }`.
 *
 * Distinguished by shape, not by an explicit version flag: only the new
 * (Task 4a+) contract's return value carries a `nextState` key -- a bare
 * owned-state object's own keys are always `design`/`page`/`selected`/
 * `blockContainerType` (see `documentState.js`), never `nextState` -- so the
 * two shapes can't collide. This "normalize on read" approach was chosen
 * over rewriting all 16 already-landed Class-A handlers (`handlers/classA.js`)
 * to return `{ nextState, sideEffects: [] }` explicitly, to keep this task's
 * diff minimal and Task 3 completely untouched.
 * @param {object | { nextState: object, sideEffects?: SideEffect[] }} result - a handler's raw return value
 * @returns {{ nextState: object, sideEffects: SideEffect[] }}
 */
export const normalizeHandlerResult = (result) =>
  result && typeof result === "object" && "nextState" in result
    ? { nextState: result.nextState, sideEffects: result.sideEffects ?? [] }
    : { nextState: result, sideEffects: [] };

/**
 * Command façade over the design mutation layer. Flag OFF (default):
 * dispatch() is a pass-through to the existing action, getState()/
 * subscribe() delegate to SiteDesignStore, and it owns no state -- BYTE-
 * IDENTICAL to Phase 5 (foundation). Flag ON (`feature.documentCore`,
 * Phase 5c Stage 1): dispatch() applies the command to DocumentStore's own
 * plain-JS state via `applyCommand`/`handlerRegistry`.
 */
const DocumentStore = {
  /** Owned plain-JS design state, used only when the owner flag is on. */
  _ownedState: createInitialState(),

  /**
   * The `sideEffects` array from the most recently applied command
   * (normalized; always an array, empty when the handler emitted none).
   * Retrievable for now via `getLastSideEffects()` -- no shim wiring drains
   * this yet (Task 10).
   * @type {SideEffect[]}
   */
  _lastSideEffects: [],

  /**
   * Route a typed command (see `route`), recording it for the DevTools
   * Dispatch tab when that's on.
   * @param {DesignCommand} command
   * @returns {*} whatever the mapped action returns (flag off)
   */
  dispatch(command) {
    return isDispatchMonitoringEnabled()
      ? monitorDispatch(command, route, DocumentStore.dispatch)
      : route(command);
  },

  /**
   * Owner-mode command application (flag-on only, called by `dispatch`).
   * Looks up the command's handler in `handlerRegistry`, normalizes its
   * return value (see `normalizeHandlerResult`), applies `nextState` to the
   * owned state, and records `sideEffects` (retrievable via
   * `getLastSideEffects()`) for the Task 10 shim to drain -- unwired in
   * this task. Unknown/unregistered types throw, mirroring the flag-off
   * unknown-command throw in `dispatch`.
   * @param {string} type
   * @param {any} payload
   * @returns {{ nextState: object, sideEffects: SideEffect[] }}
   */
  applyCommand(type, payload) {
    const handler = handlerRegistry[type];
    if (!handler) {
      throw new Error(`DocumentStore: unregistered owner command type "${type}"`);
    }
    const { nextState, sideEffects } = normalizeHandlerResult(handler(this._ownedState, payload));
    this._ownedState = nextState;
    this._lastSideEffects = sideEffects;
    return { nextState, sideEffects };
  },

  /**
   * The `sideEffects` array from the most recently applied command. Always
   * an array (empty when none were emitted, or before any command has run).
   * @returns {SideEffect[]}
   */
  getLastSideEffects() {
    return this._lastSideEffects;
  },

  /**
   * Owned state read in the legacy `getCurrentState()` shape (plain-JS).
   * Owner-mode only; meaningless while flag-off (state is never populated).
   * @returns {import("./documentState").DocumentStatePlain}
   */
  getCurrentStatePlain() {
    return computeCurrentStatePlain(this._ownedState);
  },

  /**
   * Owned state write in the legacy `applyState(data)` shape (plain-JS),
   * an unconditional overwrite. Owner-mode only.
   * @param {import("./documentState").DocumentStatePlain} next
   * @returns {void}
   */
  setStatePlain(next) {
    this._ownedState = computeSetStatePlain(this._ownedState, next);
  },

  /**
   * Current design state as a plain-JS snapshot (the future document shape).
   * Delegates to SiteDesignStore.getCurrentState() and de-Immutables it.
   * @returns {{designData: object, pageData: object, selected: any}}
   */
  getState() {
    const s = SiteDesignStore.getCurrentState();
    const toPlain = (v) => (v && typeof v.toJS === "function" ? v.toJS() : v);
    return {
      designData: toPlain(s?.designData),
      pageData: toPlain(s?.pageData),
      selected: toPlain(s?.selected),
    };
  },

  /**
   * Subscribe to design changes. In this phase the selector is accepted for
   * forward-compatibility but callbacks fire on any design change (coarse);
   * ID/path-scoped filtering arrives with the backing-store swap (5c).
   * @param {any} _selector - ids/paths (reserved; unused in this phase)
   * @param {() => void} cb
   * @returns {() => void} unsubscribe
   */
  subscribe(_selector, cb) {
    return SiteDesignStore.listen(() => cb());
  },
};

export default DocumentStore;
